import { environment } from '@/config/environment';

const OTAMA_REGISTRATION_QUEUE_KEY = 'otama-registration-sync-queue';
const OTAMA_SYNC_ACTION = 'otama_game_registration';
let registrationSyncInstalled = false;

export type SessionPayload = {
  tapCount: number;
  combo: number;
  energy: number;
};

export type OtamaRegistrationPayload = {
  sessionId: string;
  name: string;
  contact: string;
  pet: 'dog' | 'cat';
  score?: number | null;
  createdAt: string;
  device?: string;
  place?: string;
};

export async function saveSessionSnapshot(payload: SessionPayload) {
  if (!environment.apiBaseUrl) {
    return { queued: true, payload };
  }

  const response = await fetch(`${environment.apiBaseUrl}/sessions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error('No se pudo persistir la sesion del juego.');
  }

  return response.json();
}

export function installOtamaRegistrationSync() {
  if (registrationSyncInstalled || typeof window === 'undefined') {
    return;
  }

  registrationSyncInstalled = true;
  window.addEventListener('online', () => {
    void flushOtamaRegistrationQueue();
  });
  void flushOtamaRegistrationQueue();
}

export async function queueOtamaRegistration(payload: OtamaRegistrationPayload) {
  const queuedPayload = normalizeRegistrationPayload(payload);

  saveRegistrationQueue(upsertRegistrationQueue(readRegistrationQueue(), queuedPayload));
  await flushOtamaRegistrationQueue();
}

export async function flushOtamaRegistrationQueue() {
  if (typeof window === 'undefined' || !environment.otamaRegistrationEndpoint) {
    return;
  }

  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return;
  }

  const queue = readRegistrationQueue();

  if (!queue.length) {
    return;
  }

  const remaining: OtamaRegistrationPayload[] = [];

  for (const payload of queue) {
    try {
      await postOtamaRegistration(payload);
    } catch {
      remaining.push(payload);
    }
  }

  saveRegistrationQueue(remaining);
}

function normalizeRegistrationPayload(payload: OtamaRegistrationPayload): OtamaRegistrationPayload {
  return {
    ...payload,
    device: payload.device || environment.otamaDeviceLabel,
    place: payload.place || environment.otamaRegistrationPlace,
  };
}

function upsertRegistrationQueue(queue: OtamaRegistrationPayload[], payload: OtamaRegistrationPayload) {
  const index = queue.findIndex((item) => item.sessionId === payload.sessionId && item.score === payload.score);

  if (index >= 0) {
    return queue.map((item, itemIndex) => (itemIndex === index ? payload : item));
  }

  return [...queue, payload].slice(-100);
}

function readRegistrationQueue(): OtamaRegistrationPayload[] {
  try {
    const stored = localStorage.getItem(OTAMA_REGISTRATION_QUEUE_KEY);

    if (!stored) {
      return [];
    }

    const parsed = JSON.parse(stored) as OtamaRegistrationPayload[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveRegistrationQueue(queue: OtamaRegistrationPayload[]) {
  try {
    localStorage.setItem(OTAMA_REGISTRATION_QUEUE_KEY, JSON.stringify(queue));
  } catch {
    // The game should keep running even when localStorage is unavailable.
  }
}

async function postOtamaRegistration(payload: OtamaRegistrationPayload) {
  const body = JSON.stringify({
    action: OTAMA_SYNC_ACTION,
    ...payload,
  });

  await fetch(environment.otamaRegistrationEndpoint, {
    method: 'POST',
    mode: 'no-cors',
    keepalive: true,
    headers: {
      'Content-Type': 'text/plain;charset=utf-8',
    },
    body,
  });
}
